import { http } from '@/http/axios';
import type { WorkspaceLanguage } from '@/i18n/workspaceLanguage';
import type { BillingCurrency, WorkspaceSubscriptionStatus } from '@/features/billing/types';
import type { CamLayoutApiDto } from '@/lib/camLayouts';
import type {
  BillingRegularizationBannerDto,
  ProductAccessStatus,
  WorkspaceIntegration,
  WorkspaceIntegrationMetadataItem,
  WorkspaceIntegrationProviderType,
  WorkspaceIntegrationSetupInput,
  WorkspaceSocialMediaLink,
  WorkspaceTeamInvitation,
  WorkspaceTeamInviteInput,
  WorkspaceTeamMember,
  WorkspaceTeamPage,
  WorkspaceTeamRoleUpdateInput,
  WorkspaceVisualIdentity,
  WorkspaceVisualIdentityFontFamily,
  WorkspaceVisualIdentityTheme,
} from '@/features/workspace/types';

export type { BillingRegularizationBannerDto };

export type CreateWorkspaceInput = {
  name: string;
  timezone?: string;
  language?: WorkspaceLanguage;
  promoCode?: string;
  referralCode?: string;
};

export type WorkspaceInput = {
  name: string;
  language?: WorkspaceLanguage;
  timezone: string;
  promoCode?: string | null;
  /** Disposition caméras par défaut du workspace, appliquée à ses nouveaux lives. */
  camLayout?: CamLayoutApiDto;
};

export type WorkspaceDto = {
  id: number;
  name: string;
  language?: WorkspaceLanguage;
  currentUserRole: 'ADMIN' | 'USER';
  timezone: string;
  subscriptionStatus?: WorkspaceSubscriptionStatus;
  billingCurrency?: BillingCurrency;
  legacyTrialEndDate?: string | null;
  promoCode?: string | null;
  productAccessStatus?: ProductAccessStatus;
  productAccessPauseEndsAt?: string | null;
  billingRegularizationBanner?: BillingRegularizationBannerDto | null;
  camLayout?: CamLayoutApiDto | null;
};

type PageResponse<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
};
type WorkspacePageResponse = PageResponse<WorkspaceDto>;

/** Réponse minimale du listing workspaces (GET paginé). */
export type WorkspacesListPage = {
  content: WorkspaceDto[];
  totalPages: number;
};

export type WorkspaceVisualIdentityInput = {
  fontFamily: WorkspaceVisualIdentityFontFamily;
  theme: WorkspaceVisualIdentityTheme;
  accentColor: string;
  backgroundColor: string;
  secondaryBackgroundColor: string;
  contrastColor: string;
  gradient1Color1: string;
  gradient1Color2: string;
  gradient2Color1: string;
  gradient2Color2: string;
  defaultIdentity: boolean;
};

export type CreateSocialMediaLinkInput = {
  url: string;
  socialMedia: string;
};

type SocialMediaLinksResponse = {
  content?: Array<{
    id: number;
    url: string;
    socialMedia: string;
    [key: string]: unknown;
  }>;
};

export const workspaceService = {
  create: (input: CreateWorkspaceInput) => http.post('/workspaces', input).then(() => undefined),
  getAll: (page = 0, size = 10): Promise<WorkspacesListPage> =>
    http.get<WorkspacePageResponse>('/workspaces', { params: { page, size } }).then((r) => ({
      content: r.data.content,
      totalPages: r.data.totalPages,
    })),
  getById: (workspaceId: number): Promise<WorkspaceDto> =>
    http.get<WorkspaceDto>(`/workspaces/${workspaceId}`).then((r) => r.data),
  update: (workspaceId: number, input: WorkspaceInput): Promise<void> =>
    http.put<void>(`/workspaces/${workspaceId}`, input).then(() => undefined),
  getVisualIdentity: (workspaceId: number): Promise<WorkspaceVisualIdentity | null> =>
    http
      .get<WorkspaceVisualIdentity>(`/workspaces/${workspaceId}/visual-identities`)
      .then((r) => r.data)
      .catch(() => null),
  updateVisualIdentity: (workspaceId: number, input: WorkspaceVisualIdentityInput) =>
    http
      .put<WorkspaceVisualIdentity>(`/workspaces/${workspaceId}/visual-identities`, input)
      .then((r) => r.data),
  uploadLogo: (workspaceId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return http
      .put<void>(`/workspaces/${workspaceId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => undefined);
  },
  createSocialMediaLink: (workspaceId: number, input: CreateSocialMediaLinkInput) =>
    http.post<void>(`/workspaces/${workspaceId}/social-media-links`, input).then(() => undefined),
  getSocialMediaLinks: (workspaceId: number) =>
    http
      .get<SocialMediaLinksResponse>(`/workspaces/${workspaceId}/social-media-links`)
      .then((r) =>
        (r.data?.content ?? []).map(
          (link) =>
            ({
              id: link.id,
              url: link.url,
              socialMedia: link.socialMedia,
            }) satisfies WorkspaceSocialMediaLink,
        ),
      )
      .catch(() => []),
  deleteSocialMediaLink: (workspaceId: number, id: number) =>
    http.delete<void>(`/workspaces/${workspaceId}/social-media-links/${id}`).then(() => undefined),
  getTeamMembers: (workspaceId: number, page = 0, size = 20): Promise<WorkspaceTeamPage> =>
    http
      .get<
        PageResponse<WorkspaceTeamMember>
      >(`/workspaces/${workspaceId}/accesses`, { params: { page, size } })
      .then((r) => r.data),
  inviteTeamMember: (
    workspaceId: number,
    input: WorkspaceTeamInviteInput,
  ): Promise<WorkspaceTeamInvitation> =>
    http
      .post<WorkspaceTeamInvitation>(`/workspaces/${workspaceId}/accesses/invitations`, input)
      .then((r) => r.data),
  updateTeamMemberRole: (
    workspaceId: number,
    userId: string,
    input: WorkspaceTeamRoleUpdateInput,
  ): Promise<void> =>
    http
      .put<void>(`/workspaces/${workspaceId}/accesses/${userId}/role`, input)
      .then(() => undefined),
  removeTeamMember: (workspaceId: number, userId: string): Promise<void> =>
    http.delete<void>(`/workspaces/${workspaceId}/accesses/${userId}`).then(() => undefined),
  transferOwnership: (
    workspaceId: number,
    input: {
      newOwnerUserIdentifier: string;
    },
  ): Promise<void> =>
    http.put<void>(`/workspaces/${workspaceId}/accesses/owner`, input).then(() => undefined),
  getIntegrations: (workspaceId: number): Promise<WorkspaceIntegration[]> =>
    http.get<WorkspaceIntegration[]>(`/workspaces/${workspaceId}/integrations`).then((r) => r.data),
  setupIntegration: (
    workspaceId: number,
    providerType: WorkspaceIntegrationProviderType,
    input: WorkspaceIntegrationSetupInput,
  ): Promise<void> =>
    http
      .put<void>(`/workspaces/${workspaceId}/integrations/${providerType}`, input)
      .then(() => undefined),
  deleteIntegration: (
    workspaceId: number,
    providerType: WorkspaceIntegrationProviderType,
  ): Promise<void> =>
    http
      .delete<void>(`/workspaces/${workspaceId}/integrations/${providerType}`)
      .then(() => undefined),
  getIntegrationMetadata: (
    workspaceId: number,
    providerType: WorkspaceIntegrationProviderType,
    metadataType: string,
  ): Promise<WorkspaceIntegrationMetadataItem[]> =>
    http
      .get<
        WorkspaceIntegrationMetadataItem[]
      >(`/workspaces/${workspaceId}/integrations/${providerType}/metadata/${metadataType}`)
      .then((r) => r.data),
};
